import { createClient } from "@supabase/supabase-js";
import { Preferences } from "@capacitor/preferences";

const url = import.meta.env.VITE_SUPABASE_URL;
const key = import.meta.env.VITE_SUPABASE_ANON_KEY;

if (!url || !key) {
  // eslint-disable-next-line no-console
  console.error("[PettoBook] Missing Supabase env vars. Set VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY in .env.local");
}

// Replace Supabase's default `processLock` (which steals between callers and
// surfaces noisy "Lock released because another request stole it" errors when
// Chrome wakes a discarded tab) with a no-op lock. Auth requests inside a
// single tab are already serialized by the JS event loop / fetch API, and we
// no longer care about cross-tab arbitration — the cost was more pain than
// safety. This is the workaround recommended by Supabase for the wake-from-
// suspend lock-stolen scenario.
const noopLock = async (_name, _acquireTimeout, fn) => await fn();

// Detect Capacitor native (iOS / Android). On native, WebView localStorage
// can be wiped by the system between cold launches — Capacitor Preferences
// writes to native UserDefaults / SharedPreferences which survives kills,
// updates and even uninstalls (Android backup). Web stays on localStorage.
const isCapacitor = typeof window !== "undefined"
  && !!window.Capacitor
  && typeof window.Capacitor.isNativePlatform === "function"
  && window.Capacitor.isNativePlatform();

const nativeStorage = {
  async getItem(k) {
    try { const { value } = await Preferences.get({ key: k }); return value ?? null; }
    catch { return null; }
  },
  async setItem(k, v) {
    try { await Preferences.set({ key: k, value: String(v) }); } catch { /* ignore */ }
  },
  async removeItem(k) {
    try { await Preferences.remove({ key: k }); } catch { /* ignore */ }
  },
};

// Native WKWebView post-OAuth wedge — supabase-js's internal request
// state machine stalls indefinitely. Wrap fetch with an AbortController
// timeout so a stuck request aborts, freeing the queue for the next call.
// Web keeps the default cross-fetch behaviour.
//
// Two-tier timeout: storage uploads (avatar / banner / grooming photos /
// signed reports) routinely take 10–30s on 4G with a 1–2 MB compressed
// JPEG and would otherwise abort mid-flight, so we give those calls 90s.
// PostgREST / Auth / Realtime keep the 12s short-circuit which catches
// real wedges fast.
const STORAGE_PATH_RE = /\/storage\/v1\//;
// Paths that count as authenticated supabase traffic. A 401 from any of
// these (except /auth/v1 itself — that's the login flow, where 401 means
// "wrong password," not an expired session) signals the access token is
// no longer valid and the app must drop back to the login screen.
// NOTE: deliberately excludes /functions/ — edge functions legitimately return
// 401/403 for their OWN authorization logic (e.g. link-pets-on-login,
// create-customer-account), and treating those as "session expired" was
// force-logging-out users mid-action. A genuinely dead token still 401s on
// /rest/ + /storage/ calls, which DO trigger the recovery.
const AUTHED_PATH_RE = /\/(rest|storage)\/v1\//;
// Notify the app of a session-expired 401 from any supabase-js call.
// IMPORTANT: only 401 (invalid/expired JWT) means a dead session. 403 means
// "valid token, action forbidden" — e.g. an RPC that RAISEs a permission/
// validation error (errcode 42501) or an RLS denial. Treating 403 as
// session-expired was force-logging-out customers on legitimate actions like
// cancelling a booking. So we fire ONLY on 401.
let _lastSessionExpiredAt = 0;
function _maybeFireSessionExpired(resp, url) {
  if (!resp || resp.status !== 401) return;
  if (!AUTHED_PATH_RE.test(url)) return;
  const now = Date.now();
  if (now - _lastSessionExpiredAt < 1000) return;
  _lastSessionExpiredAt = now;
  try {
    if (typeof window !== "undefined") {
      window.dispatchEvent(new CustomEvent("petos:session-expired", { detail: { reason: resp.status + ":" + url.split("?")[0].slice(-60) } }));
    }
  } catch { /* ignore */ }
}
const nativeFetch = async (input, init) => {
  const url = typeof input === "string" ? input : (input?.url || "");
  const isStorage = STORAGE_PATH_RE.test(url);
  const timeoutMs = isStorage ? 90000 : 12000;
  const controller = new AbortController();
  const tid = setTimeout(() => controller.abort(), timeoutMs);
  try {
    const resp = await fetch(input, { ...(init || {}), signal: controller.signal });
    _maybeFireSessionExpired(resp, url);
    return resp;
  } finally {
    clearTimeout(tid);
  }
};
// Web client also runs through a thin wrapper so a 401 from any
// supabase-js call (.from / .rpc / .storage / .functions) feeds the same
// session-expired recovery hook. Without this, the only 401 detector was
// api.rawWrite, which misses all PostgREST traffic via supabase-js itself.
const webFetch = async (input, init) => {
  const url = typeof input === "string" ? input : (input?.url || "");
  // Bound web requests too. After the tab/PWA wakes from being backgrounded,
  // the old HTTP/2 connection can be half-dead and a fetch (e.g. saving a
  // booking) hangs indefinitely — the app looks frozen. A timeout aborts the
  // wedged call so it surfaces as a retryable error instead of an endless hang.
  // Storage uploads get a longer window; everything else 15s.
  const isStorage = STORAGE_PATH_RE.test(url);
  const timeoutMs = isStorage ? 90000 : 15000;
  const controller = new AbortController();
  const tid = setTimeout(() => controller.abort(), timeoutMs);
  try {
    const resp = await fetch(input, { ...(init || {}), signal: controller.signal });
    _maybeFireSessionExpired(resp, url);
    return resp;
  } finally {
    clearTimeout(tid);
  }
};

export const supabase = createClient(url, key, {
  global: { fetch: isCapacitor ? nativeFetch : webFetch },
  auth: {
    persistSession: true,
    // Auto-refresh is DISABLED on both platforms. Two reasons:
    //   Native: supabase-js's auto-refresh wedges the WKWebView request
    //     queue against the new OAuth session (see setInterval below).
    //   Web/PWA: supabase-js's auto-refresh races OUR serialized refreshes
    //     (sessionPoll / onSessionExpired / resume / heartbeat all call
    //     ensureFreshSession in auth.js). Two callers racing → Supabase's
    //     rotation invalidates the loser's refresh_token → the loser gets
    //     "refresh token already used" → classified as authInvalid → user
    //     is bounced to the login screen even though the session was fine.
    //     Delegate ALL refresh authority to ensureFreshSession (which
    //     serializes via _refreshInflight) and drive the schedule from
    //     the setInterval below. Eliminates the wake-from-idle logout bug.
    autoRefreshToken: false,
    detectSessionInUrl: !isCapacitor,
    storageKey: "petos-auth",
    storage: isCapacitor ? nativeStorage : undefined,
    lock: noopLock,
  },
  // On native, realtime's heartbeat-reconnect loop ties up the WKWebView
  // request queue right after OAuth — every PostgREST call blocks behind
  // the reconnecting socket. Slow the heartbeat down so it doesn't fight
  // the boot fetch storm. Live updates still work; they just notice
  // disconnects ~30s slower.
  realtime: isCapacitor
    ? { params: { eventsPerSecond: 5 }, heartbeatIntervalMs: 30000, reconnectAfterMs: () => 8000 }
    : { params: { eventsPerSecond: 10 } },
});

// Manual token refresh — supabase-js autoRefresh is off on BOTH platforms
// (native wedges the WKWebView; web races our serialized refreshes into a
// token-rotation logout). 50-min interval gives a 10-min safety margin
// before the 1-hour access_token expires. Both platforms route through the
// same single serialized refresh authority (ensureFreshSession in auth.js).
if (typeof window !== "undefined") {
  setInterval(() => {
    import("./auth.js").then(m => m.ensureFreshSession()).catch(() => { /* best effort */ });
  }, 50 * 60 * 1000);
}

// Keep window.__petosAccessToken in sync with whatever session
// supabase-js has loaded. api.js's raw-fetch writes read this global
// instead of awaiting getSession() (which can wedge on native).
// Without this listener the token captured at sign-in goes stale after
// a refresh, and writes silently fall back to the anon key — which
// fails RLS and surfaces as "權限不足或未變更".
if (typeof window !== "undefined") {
  supabase.auth.onAuthStateChange((_event, session) => {
    try { window.__petosAccessToken = session?.access_token || null; } catch { /* ignore */ }
  });
  // Hydrate on module load — onAuthStateChange's INITIAL_SESSION fires
  // async, but we also poll the current session synchronously so the
  // very first write after a cold reload has a token.
  supabase.auth.getSession().then(({ data }) => {
    try { window.__petosAccessToken = data?.session?.access_token || null; } catch { /* ignore */ }
  }).catch(() => { /* ignore */ });
}

// One-time migration: copy any existing session token from localStorage to
// Preferences on first native launch after this change ships, so existing
// users don't get bounced to login on the upgrade.
if (isCapacitor && typeof localStorage !== "undefined") {
  const k = "petos-auth";
  try {
    const legacy = localStorage.getItem(k);
    if (legacy) {
      Preferences.get({ key: k }).then(({ value }) => {
        if (!value) {
          Preferences.set({ key: k, value: legacy }).catch(() => {});
        }
      }).catch(() => {});
    }
  } catch { /* ignore */ }
}

// Suppress noisy unhandled-rejection logs from any stale lock that managed to
// throw before our auth.js retry wrapper saw it. They're cosmetic now that the
// lock itself is a no-op, but old in-flight rejections may still surface once.
if (typeof window !== "undefined") {
  window.addEventListener("unhandledrejection", (ev) => {
    const msg = String(ev?.reason?.message || ev?.reason || "");
    if (/Lock.*was released because another request stole it/i.test(msg)) {
      ev.preventDefault();
    }
  });
}

// When Chrome's Memory Saver / tab-discard wakes the page back up, the old
// HTTP/2 connection to Supabase is often dead. Proactively warm the
// connection with a tiny ping so the user's first action after coming back
// (typically clicking 登入) doesn't hit "Failed to fetch" on a stale socket.
if (typeof document !== "undefined" && typeof window !== "undefined") {
  let lastWake = 0;
  const warmUp = () => {
    const now = Date.now();
    if (now - lastWake < 30000) return; // throttle to once per 30s
    lastWake = now;
    // Cheap unauthenticated request that establishes a fresh TCP/TLS handshake.
    fetch(url + "/auth/v1/health", {
      method: "GET",
      headers: { apikey: key },
      cache: "no-store",
    }).catch(() => { /* best effort */ });
  };
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible") warmUp();
  });
  window.addEventListener("focus", warmUp);
  window.addEventListener("online", warmUp);
}
